import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { OverlayPanel } from 'primereact/overlaypanel';
import { CalendarIcon, CaretIcon } from '../../../../components/icons';

// Same calendar look as the Global Schedule week picker.
import '../../../../components/WeekSelector/WeekSelector.scss';
import './OnDemandStaffShiftHistory.scss';

const EMPTY_RANGE = { from: null, to: null };
const DISPLAY_FORMAT = 'MM/DD/YYYY';

// "Any date" filter for the Shift History page: From / To dates, typed as MM/DD/YYYY or picked as a range on the
// calendar below them, which is always open. Nothing reaches the page until Apply; Clear drops the applied range.
// The draft resets to the applied range whenever the panel closes.
const ShiftHistoryDateFilter = ({ dateRange, onApply }) => {
	const overlayPanelRef = useRef(null);
	const [draftRange, setDraftRange] = useState(dateRange);

	const togglePanel = useCallback((event) => overlayPanelRef.current?.toggle(event), []);
	const resetDraft = useCallback(() => setDraftRange(dateRange), [dateRange]);

	const onFromChange = useCallback((event) => setDraftRange((range) => ({ ...range, from: event.value || null })), []);
	const onToChange = useCallback((event) => setDraftRange((range) => ({ ...range, to: event.value || null })), []);
	// The range calendar sends [start, end], with end null until the second date is picked.
	const onRangeChange = useCallback((event) => {
		const [from = null, to = null] = event.value || [];
		setDraftRange({ from, to });
	}, []);

	const handleApply = useCallback(() => {
		onApply(draftRange);
		overlayPanelRef.current?.hide();
	}, [onApply, draftRange]);

	const handleClear = useCallback(() => {
		setDraftRange(EMPTY_RANGE);
		onApply(EMPTY_RANGE);
		overlayPanelRef.current?.hide();
	}, [onApply]);

	// The page only lists shifts from the past year, so no date can go further back.
	const oldestSelectableDate = useMemo(() => moment().subtract(1, 'year').startOf('day').toDate(), []);
	const toMinDate = draftRange.from || oldestSelectableDate;

	const calendarRange = useMemo(
		() => (draftRange.from ? [draftRange.from, draftRange.to] : null),
		[draftRange.from, draftRange.to]
	);

	const isApplyDisabled = !draftRange.from || !draftRange.to || moment(draftRange.from).isAfter(draftRange.to, 'day');

	const buttonLabel = useMemo(
		() =>
			dateRange.from && dateRange.to
				? `${moment(dateRange.from).format(DISPLAY_FORMAT)} - ${moment(dateRange.to).format(DISPLAY_FORMAT)}`
				: 'Any date',
		[dateRange]
	);

	const draftLabel = draftRange.from
		? `${moment(draftRange.from).format('MMM D')} - ${draftRange.to ? moment(draftRange.to).format('MMM D') : ''}`
		: 'No dates selected';

	return (
		<div className="shift-history-date-filter">
			<Button className="shift-history-date-filter__button" onClick={togglePanel} aria-label="Filter shifts by date">
				<CalendarIcon width={18} height={18} />
				<span>{buttonLabel}</span>
				<CaretIcon pointTo="down" />
			</Button>
			<OverlayPanel
				ref={overlayPanelRef}
				onHide={resetDraft}
				className="week-selector shift-history-date-filter__panel"
			>
				<div className="shift-history-date-filter__fields">
					<label htmlFor="shift-history-from">
						From
						<Calendar
							inputId="shift-history-from"
							value={draftRange.from}
							onChange={onFromChange}
							minDate={oldestSelectableDate}
							maxDate={draftRange.to}
							dateFormat="mm/dd/yy"
							placeholder={DISPLAY_FORMAT}
							showOnFocus={false}
						/>
					</label>
					<label htmlFor="shift-history-to">
						To
						<Calendar
							inputId="shift-history-to"
							value={draftRange.to}
							onChange={onToChange}
							minDate={toMinDate}
							dateFormat="mm/dd/yy"
							placeholder={DISPLAY_FORMAT}
							showOnFocus={false}
						/>
					</label>
				</div>
				<Calendar
					value={calendarRange}
					onChange={onRangeChange}
					selectionMode="range"
					showOtherMonths
					numberOfMonths={1}
					minDate={oldestSelectableDate}
					readOnlyInput
					inline
				/>
				<div className="week-selector-actions">
					<div className="week-selector-actions__text">{draftLabel}</div>
					<div className="week-selector-actions__buttons">
						<button type="button" className="button button-secondary" onClick={handleClear}>
							Clear
						</button>
						<button type="button" className="button button-primary" onClick={handleApply} disabled={isApplyDisabled}>
							Apply
						</button>
					</div>
				</div>
			</OverlayPanel>
		</div>
	);
};

export default memo(ShiftHistoryDateFilter);
